/**
 * The motion contract for a batch of rows arriving in an issues list, shared so
 * the three surfaces that receive a bulk create cannot drift from each other.
 *
 * All three land rows the same way and all three used to land them in one
 * frame: the extension sidebar (a11y, design tokens, Auto Review and compare
 * panels), the team-review drawer (`review-app`, whose Accessibility and Auto
 * Review panels file through the extension's own bulk routes), and the
 * dashboard Issues table (the re-scan email's "Add N issues" modal).
 *
 * WHAT IS SHARED AND WHAT IS NOT. The pacing and the slot arithmetic live here.
 * WHICH rows are new is a per-surface question with a per-surface answer: the
 * extension reads the server's `createdIds` off the create call, while team
 * review and the dashboard diff the list they already hold. A surface that
 * guessed at membership would cascade the wrong rows; a surface that invented
 * its own pacing would make the product feel like three products.
 *
 * WHY THE CASCADE STOPS. PostHog `bulk_create_completed` over 180 days (53
 * runs, measured 2026-09-13): p50 7 created, p90 27, p99 155, max 201. At 50ms
 * a row, an uncapped cascade of that worst real run runs for 10.05 seconds.
 * Rows past the first screenful are below the fold, so animating them buys
 * nothing and costs the count its pacing.
 */
/**
 * Gap between two arriving rows. The design band is 30-80ms: below it the rows
 * read as one block, above it the list feels like it is loading slowly.
 */
export const CASCADE_STEP_MS = 50;
/**
 * How many arrivals actually animate, because an animation below the fold is
 * work nobody watches.
 *
 * MEASURED, not chosen: the 2026-09-13 browser run read the extension sidebar's
 * Issues list at a 54px row in a 485px scroll viewport, so 8 whole rows are on
 * screen at that height (`docs/test-reports/2026-09-13-bulk-create-cascade`).
 * 12 sits deliberately above that — a taller sidebar shows more, and a row just
 * under the fold should still land with its group rather than be there already
 * when the reader scrolls one notch. It is also the span ceiling: 12 x 50ms
 * caps the cascade at 600ms however many rows arrive, which is what keeps the
 * measured worst real run (201 created) from running for ten seconds.
 */
export const CASCADE_VISIBLE_SLOTS = 12;
/**
 * How long ONE thing takes to enter — a row sliding into the list, and the pin
 * that flies into that row as it lands.
 *
 * One number rather than two because they are two halves of one gesture: the
 * pin is arriving AT the row while the row is arriving, so a second duration
 * would visibly desynchronise them. The row entrance CSS interpolates this
 * (review-app issue-table/styles.ts) instead of restating 220, which also
 * keeps `arrivalsHoldMs` below honest — a hold shorter than the animation it
 * protects is the bug that function exists to fix.
 */
export const ENTRANCE_MS = 220;
/**
 * How long the whole cascade runs, which is what the count is paced to.
 * Bounded by the visible slots, so the 201-row run costs exactly what a full
 * screen costs.
 */
export function cascadeSpanMs(arrivalCount, slots = CASCADE_VISIBLE_SLOTS, stepMs = CASCADE_STEP_MS) {
    if (!Number.isFinite(arrivalCount) || arrivalCount <= 0)
        return 0;
    return Math.min(Math.floor(arrivalCount), slots) * stepMs;
}
/**
 * How long a cascade is still RUNNING, and therefore how long its arrivals
 * must stay marked.
 *
 * `cascadeSpanMs` measures when the last row STARTS; this adds the entrance
 * that row then has to run, which is the difference between a cascade that
 * finishes and one that gets cut off at the knees.
 *
 * MEASURED DEFECT (2026-09-13, team review, real Chromium): a row gained
 * `.lr-row--entering` at +6206ms and lost it at +6388ms, 182ms into a 220ms
 * animation, because a second refetch recomputed arrivals as empty. At one
 * arrival that is invisible; at twelve, slot 11 has not started at 182ms and
 * would never animate at all.
 */
export function arrivalsHoldMs(arrivalCount, slots = CASCADE_VISIBLE_SLOTS, stepMs = CASCADE_STEP_MS) {
    const span = cascadeSpanMs(arrivalCount, slots, stepMs);
    // cascadeSpanMs already returns 0 for junk and for "nothing arrived", and a
    // hold of 0 is the honest answer there: there is no entrance to protect.
    if (span <= 0)
        return 0;
    // span is the START of the slot AFTER the last one, so back off one step to
    // get the last row's own start, then let it run its entrance.
    return span - stepMs + ENTRANCE_MS;
}
/**
 * Hand out stagger slots for one render pass, in CALL order.
 *
 * Call order is render order, and render order is what the reader sees. The
 * order the server created the rows in is NOT usable here: every one of these
 * lists sorts and groups before it paints (the extension sidebar by severity
 * with the newest floated, then by page), so a row created first can render
 * third, and paying out slots by creation order would run the cascade in an
 * order nobody can follow.
 *
 * A row that did not just arrive returns null and spends no slot, so a
 * pre-existing row sitting between two arrivals cannot open a gap in the
 * stagger.
 *
 * Returns null past `slots`: those rows are below the fold and are simply
 * there when the reader scrolls to them.
 */
export function createCascadeAssigner(arrivals, slots = CASCADE_VISIBLE_SLOTS) {
    const isArrival = new Set(arrivals);
    let next = 0;
    return (issueId) => {
        if (!isArrival.has(issueId))
            return null;
        if (next >= slots)
            return null;
        return next++;
    };
}
/**
 * The ids present in `after` but not in `before`, in `after`'s own order.
 *
 * The membership answer for a surface that holds its list rather than the
 * create's reply (team review, the dashboard). Order does not matter to the
 * caller — `createCascadeAssigner` re-orders by render anyway — but keeping
 * `after`'s order makes the result readable in a log.
 */
export function newIssueIds(before, after) {
    const had = new Set(before);
    return after.filter((id) => !had.has(id));
}
//# sourceMappingURL=cascade.js.map